import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import {
  ScanEventsService,
  PaginatedPickupScansResponse,
  GetPickupScansParams,
  ExportPickupScansParams,
} from '@app/shared/services/scan-events.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { DateTime } from 'luxon';
import { ScanEventsAirportCodes, PickupProviders, PICKUP_SCANS_DEFAULT_PAGE_SIZE } from '@app/shared/constants';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-pickup-scans-reports',
  templateUrl: './pickup-scans-reports.component.html',
  styleUrls: ['./pickup-scans-reports.component.scss'],
})
export class PickupScansReportsComponent implements OnInit, OnDestroy {
  @Input() user: AdminUser;

  public isAppleExpressUser: boolean = false;
  public canExportCsv: boolean = false;
  public loading: boolean = false;
  public hasError: boolean = false;
  public errorMessage: string = '';
  // Export loading state
  public exporting: boolean = false;

  // Fallback location codes from constants
  private fallbackLocationCodes = ScanEventsAirportCodes;
  public pickupProviders = PickupProviders;

  // Date range (default last 6 days)
  public daterange = {
    start: DateTime.now().minus({ days: 6 }).startOf('day').toJSDate(),
    end: DateTime.now().endOf('day').toJSDate(),
  };

  public range = new FormGroup({
    start: new FormControl<Date | null>(this.daterange.start),
    end: new FormControl<Date | null>(this.daterange.end),
  });

  // Date picker constraints: max 6 days range, no future dates
  private readonly today: Date = DateTime.now().endOf('day').toJSDate();
  public maxDate: Date = this.today;

  // Filter selections
  public selectedLocations: string[] = [];
  public selectedProviders: string[] = [];

  // Pagination
  public currentPage: number = 1;
  public pageSize: number = PICKUP_SCANS_DEFAULT_PAGE_SIZE;

  // Response data
  public responseData: PaginatedPickupScansResponse | null = null;

  // Table columns (dynamically generated from date range)
  public dateColumns: string[] = [];

  // Subscription management
  private destroy$ = new Subject<void>();

  constructor(
    private scanEventsService: ScanEventsService,
    private bannerNotificationsService: BannerNotificationsService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.isAppleExpressUser = this.userService.hasAuthScope(this.user, [
      AdminScopes.VIEW_SCAN_EVENTS_PICKUPS_APPLEEXPRESS,
    ]);
    this.canExportCsv = this.userService.hasAuthScope(this.user, [AdminScopes.MODIFY_SCAN_EVENTS_PICKUPS]);
    this.loadData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Getter for displayed columns (fixes arrow function in template).
   */
  public get displayedColumns(): string[] {
    return ['company'].concat(this.dateColumns.map((_, i) => 'date_' + i)).concat(['total']);
  }

  /**
   * Getter for footer columns.
   */
  public get footerColumns(): string[] {
    return ['company-footer'].concat(this.dateColumns.map((_, i) => 'date_' + i + '_footer')).concat(['total_footer']);
  }

  /**
   * Getter for location filter options.
   * Uses available locations from response data, fallback to constants.
   */
  public get locationOptions(): string[] {
    if (this.responseData?.availableLocations?.length) {
      return this.responseData.availableLocations;
    }
    return this.fallbackLocationCodes;
  }

  /**
   * Get display timezone from browser.
   */
  private getDisplayTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
    } catch {
      return 'America/Toronto';
    }
  }

  /**
   * Load pickup scans data from API.
   */
  public loadData(): void {
    if (!this.range.value.start || !this.range.value.end) {
      return;
    }

    this.loading = true;
    this.hasError = false;

    const localTimezone = this.getDisplayTimezone();
    const params: GetPickupScansParams = {
      startDate: DateTime.fromJSDate(this.range.value.start).startOf('day').toUTC().toISO(),
      endDate: DateTime.fromJSDate(this.range.value.end).endOf('day').toUTC().toISO(),
      localTimezone,
      locations: this.selectedLocations.length > 0 ? this.selectedLocations : undefined,
      pickupProviders: this.selectedProviders.length > 0 ? this.selectedProviders : undefined,
      page: this.currentPage,
      limit: this.pageSize,
    };

    this.scanEventsService.getPickupScans(params).subscribe({
      next: (response: PaginatedPickupScansResponse) => {
        this.responseData = response;
        this.generateDateColumns();
        this.loading = false;
      },
      error: (error) => {
        this.hasError = true;
        this.errorMessage = typeof error === 'string' ? error : 'Failed to load pickup scans data';
        this.bannerNotificationsService.error(this.errorMessage);
        this.loading = false;
      },
    });
  }

  /**
   * Handle start date selection in the date range picker.
   * Constrains maxDate to start + 6 days (or today, whichever is earlier)
   * so the end date selection is restricted to a 6-day window.
   */
  public onStartDateChange(event: any): void {
    if (event.value) {
      const startPlus6 = DateTime.fromJSDate(event.value).plus({ days: 6 }).endOf('day').toJSDate();
      this.maxDate = startPlus6 < this.today ? startPlus6 : this.today;

      // If existing end date exceeds new max, clear it
      const endDate = this.range.get('end')?.value;
      if (endDate && endDate > this.maxDate) {
        this.range.get('end')?.setValue(null);
      }
    }
  }

  /**
   * Handle date range picker opened.
   * Resets maxDate to today when both dates are set, so the user
   * can freely pick any past date as a new start date.
   */
  public onPickerOpened(): void {
    const startDate = this.range.get('start')?.value;
    const endDate = this.range.get('end')?.value;

    if (startDate && endDate) {
      // Both dates set: clicking will start a new range, allow any start date
      this.maxDate = this.today;
    } else if (startDate && !endDate) {
      // Only start set: user is continuing end date selection, keep constraint
      const startPlus6 = DateTime.fromJSDate(startDate).plus({ days: 6 }).endOf('day').toJSDate();
      this.maxDate = startPlus6 < this.today ? startPlus6 : this.today;
    } else {
      this.maxDate = this.today;
    }
  }

  /**
   * Handle page change from paginator.
   */
  public onPageChange(event: any): void {
    this.currentPage = event.pageIndex + 1; // Material paginator is 0-indexed
    this.pageSize = event.pageSize;
    this.loadData();
  }

  /**
   * Generate date column headers from response data.
   * Backend now returns dates normalized to user's local timezone, so no conversion needed.
   */
  private generateDateColumns(): void {
    if (this.responseData?.dailyTotalPickupScans?.length) {
      // Backend returns dates already in local timezone format (YYYY-MM-DD)
      // Just format for display without timezone conversion
      this.dateColumns = this.responseData.dailyTotalPickupScans.map((d) =>
        DateTime.fromISO(d.datetime).toFormat('MMM dd')
      );
    } else {
      this.dateColumns = [];
    }
  }

  /**
   * Get formatted date header with day name (e.g., "Mon, Jan 5").
   */
  public getFormattedDateHeader(dateIndex: number): string {
    if (this.responseData?.dailyTotalPickupScans?.[dateIndex]) {
      const dt = DateTime.fromISO(this.responseData.dailyTotalPickupScans[dateIndex].datetime);
      return dt.toFormat('ccc, MMM d');
    }
    return '';
  }

  /**
   * Get pickup count for a company on a specific date.
   */
  public getPickupCount(companyData: any, dateIndex: number): number {
    if (companyData?.aggregatedData?.[dateIndex]) {
      return companyData.aggregatedData[dateIndex].pickupScansCount;
    }
    return 0;
  }

  /**
   * Get total cost for a company on a specific date (pickupScansCost + otherPickupScansCost).
   */
  public getPickupCost(companyData: any, dateIndex: number): string {
    if (companyData?.aggregatedData?.[dateIndex]) {
      const cost = companyData.aggregatedData[dateIndex].totalCostPerDay || 0;
      return cost.toFixed(2);
    }
    return '0.00';
  }

  /**
   * Get total scans for a company on a specific date (pickupScansCount + otherPickupScansCount).
   */
  public getTotalScansPerDay(companyData: any, dateIndex: number): number {
    if (companyData?.aggregatedData?.[dateIndex]) {
      return companyData.aggregatedData[dateIndex].totalScansPerDay || 0;
    }
    return 0;
  }

  /**
   * Get daily total scans for a specific date index (combined PU + other).
   */
  public getDailyTotal(dateIndex: number): number {
    if (this.responseData?.dailyTotalPickupScans?.[dateIndex]) {
      return this.responseData.dailyTotalPickupScans[dateIndex].combinedTotalDailyPickupScans;
    }
    return 0;
  }

  /**
   * Get daily total cost for a specific date index (combined PU + other costs).
   */
  public getDailyTotalCost(dateIndex: number): string {
    if (this.responseData?.dailyTotalPickupScans?.[dateIndex]) {
      const cost = this.responseData.dailyTotalPickupScans[dateIndex].combinedTotalDailyPickupScansCost || 0;
      return cost.toFixed(2);
    }
    return '0.00';
  }

  /**
   * Get total cost for a company across all dates (sum of totalCostPerDay).
   */
  public getCompanyTotalCost(companyData: any): string {
    if (!companyData?.aggregatedData?.length) {
      return '0.00';
    }
    let totalCost = 0;
    for (const day of companyData.aggregatedData) {
      totalCost += day.totalCostPerDay || 0;
    }
    return totalCost.toFixed(2);
  }

  /**
   * Get total scans for a company across all dates (sum of totalScansPerDay).
   */
  public getCompanyTotalScans(companyData: any): number {
    if (!companyData?.aggregatedData?.length) {
      return 0;
    }
    let totalScans = 0;
    for (const day of companyData.aggregatedData) {
      totalScans += day.totalScansPerDay || 0;
    }
    return totalScans;
  }

  /**
   * Get grand total scans for all companies across all dates (PU + other).
   */
  public getGrandTotalPickups(): number {
    return this.responseData?.totalDailyTotalPickupScansForAllCompanies || 0;
  }

  /**
   * Get grand total cost for all companies across all dates.
   */
  public getGrandTotalPickupsCost(): string {
    return (this.responseData?.totalDailyTotalPickupScansForAllCompaniesCost || 0).toFixed(2);
  }

  /**
   * Format number for display.
   */
  public formatNumber(value: number): string {
    return value.toLocaleString();
  }

  /**
   * Export all pickup scans data to CSV via backend.
   * Exports all companies (not just current page) within the selected date range.
   */
  public exportToCsv(): void {
    if (!this.range.value.start || !this.range.value.end) {
      return;
    }

    this.exporting = true;
    const localTimezone = this.getDisplayTimezone();

    const params: ExportPickupScansParams = {
      startDate: DateTime.fromJSDate(this.range.value.start).startOf('day').toUTC().toISO(),
      endDate: DateTime.fromJSDate(this.range.value.end).endOf('day').toUTC().toISO(),
      localTimezone,
      locations: this.selectedLocations.length > 0 ? this.selectedLocations : undefined,
      pickupProviders: this.selectedProviders.length > 0 ? this.selectedProviders : undefined,
    };

    this.scanEventsService.exportPickupScansCsv(params).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `pickup-scans-${DateTime.fromISO(params.startDate, { zone: 'UTC' })
            .setZone(localTimezone)
            .toFormat('yyyy-MM-dd')}-${DateTime.fromISO(params.endDate, { zone: 'UTC' })
            .setZone(localTimezone)
            .toFormat('yyyy-MM-dd')}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.exporting = false;
      },
      error: (error) => {
        this.bannerNotificationsService.error('Failed to export CSV. Please try again.');
        this.exporting = false;
      },
    });
  }
}
